/**
 * CostDashboardPage (P3-F Doc 41) — analytics charts.
 */
import { Coins, Loader2 } from "lucide-react";
import * as React from "react";

import {
  type ByDayResponse,
  type ByProviderResponse,
  type ByUserResponse,
  costApi,
} from "@/api/costAnalytics";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Donut } from "@/components/ui/svg-charts";
import { useUIStore } from "@/stores/ui";

const RANGES = [
  { value: 1, label: "Hôm nay" },
  { value: 7, label: "7 ngày" },
  { value: 30, label: "30 ngày" },
  { value: 90, label: "90 ngày" },
];

function formatUsd(v: number): string {
  if (v >= 1) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(3)}`;
  return `$${v.toFixed(4)}`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function CostDashboardPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [days, setDays] = React.useState(7);
  const [byDay, setByDay] = React.useState<ByDayResponse | null>(null);
  const [byUser, setByUser] = React.useState<ByUserResponse | null>(null);
  const [byProvider, setByProvider] = React.useState<ByProviderResponse | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void Promise.all([
      costApi.byDay(days).catch(() => null),
      costApi.byUser(days, 10).catch(() => null),
      costApi.byProvider(days).catch(() => null),
    ]).then(([d, u, p]) => {
      if (cancelled) return;
      if (!d || !u || !p) {
        pushToast({
          variant: "destructive",
          title: "Một số queries thất bại",
        });
      }
      setByDay(d);
      setByUser(u);
      setByProvider(p);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [days, pushToast]);

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Coins className="size-5 text-primary" />
            Cost Analytics
          </h1>
          <p className="text-sm text-muted-foreground">
            Chi phí LLM theo ngày / user / provider. Cache 5 phút Redis.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              className={
                "text-xs px-2.5 py-1 rounded-md border transition-colors " +
                (days === r.value
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background hover:bg-muted")
              }
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {/* Summary cards */}
      {byDay ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            label="Tổng chi phí"
            value={formatUsd(byDay.total_cost_usd)}
            hint={`${byDay.days} ngày`}
          />
          <SummaryCard
            label="Tổng runs"
            value={String(byDay.total_runs)}
            hint="agent calls"
          />
          <SummaryCard
            label="Tokens in"
            value={formatNumber(byDay.total_tokens_in)}
            hint="prompt tokens"
          />
          <SummaryCard
            label="Tokens out"
            value={formatNumber(byDay.total_tokens_out)}
            hint="completion tokens"
          />
        </div>
      ) : null}

      {loading ? (
        <Card>
          <CardContent className="p-8 flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Đang tải analytics...
          </CardContent>
        </Card>
      ) : null}

      {/* By day chart */}
      {byDay && byDay.points.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Chi phí theo ngày</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChart
              data={byDay.points.map((p) => p.cost_usd)}
              labels={byDay.points.map((p) => shortDate(p.date))}
              formatValue={formatUsd}
              height={140}
            />
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* By provider donut */}
        {byProvider && byProvider.items.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Theo Provider / Model</CardTitle>
            </CardHeader>
            <CardContent>
              <Donut
                data={byProvider.items.map((p) => ({
                  label: `${p.provider} / ${p.model}`,
                  value: p.cost_usd,
                }))}
                size={140}
              />
            </CardContent>
          </Card>
        ) : null}

        {/* By user list */}
        {byUser && byUser.items.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Top users</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {byUser.items.map((u, i) => (
                <div
                  key={u.user_id}
                  className="flex items-center gap-2 text-xs py-1 border-b last:border-b-0"
                >
                  <span className="text-muted-foreground w-5">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {u.display_name ?? u.email ?? u.user_id.slice(0, 8)}
                    </div>
                    {u.email && u.display_name ? (
                      <div className="text-[10px] text-muted-foreground truncate">
                        {u.email}
                      </div>
                    ) : null}
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    {u.runs} runs
                  </Badge>
                  <span className="text-xs font-mono shrink-0 w-16 text-right">
                    {formatUsd(u.cost_usd)}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : null}
      </div>

      {!loading && byDay && byDay.points.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            Không có data trong {days} ngày. Hãy chat với agent để có cost data.
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  hint: string;
}

function SummaryCard({ label, value, hint }: SummaryCardProps): JSX.Element {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase font-semibold text-muted-foreground">
          {label}
        </div>
        <div className="text-xl font-bold mt-1">{value}</div>
        <div className="text-[10px] text-muted-foreground">{hint}</div>
      </CardContent>
    </Card>
  );
}
